import React, { useState, useEffect } from 'react';
import {
  VStack,
  HStack,
  Heading,
  Text,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Button,
  Alert,
  AlertIcon,
  Spinner,
  useToast,
  Card,
  CardBody,
  useColorModeValue,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { useApi } from '../../../contexts/ApiContext';
import { useUserProfile } from '../../../contexts/userprofile/UserProfileContext';
import {
  CordeCommonModelsDtoUserprofileUpdateUserProfileRequest as UpdateUserProfileRequest,
} from '../../../api/generated/v1/api';
import { UserProfile } from '../../../contexts/userprofile/UserProfileContext';

interface ProfileSettingsProps {
  userId: string;
  profile: UserProfile | null;
  onMarkChanged: (hasChanges: boolean) => void;
  onClearChanges: () => void;
}

interface ProfileFormData {
  first_name: string;
  middle_name: string;
  last_name: string;
  display_name: string;
  bio: string;
  date_of_birth: string;
  gender: string;
  location: string;
  language: string;
  timezone: string;
}

export const ProfileSettings: React.FC<ProfileSettingsProps> = ({
  userId,
  profile,
  onMarkChanged,
  onClearChanges,
}) => {
  const { t } = useTranslation();
  const { accessToken } = useAuth();
  const api = useApi();
  const toast = useToast();
  const { reloadProfile } = useUserProfile();

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<ProfileFormData>({
    first_name: '',
    middle_name: '',
    last_name: '',
    display_name: '',
    bio: '',
    date_of_birth: '',
    gender: '',
    location: '',
    language: 'ru',
    timezone: 'UTC',
  });
  const [originalData, setOriginalData] = useState<ProfileFormData>({
    first_name: '',
    middle_name: '',
    last_name: '',
    display_name: '',
    bio: '',
    date_of_birth: '',
    gender: '',
    location: '',
    language: 'ru',
    timezone: 'UTC',
  });

  // Initialize form data from profile
  useEffect(() => {
    if (profile) {
      const data: ProfileFormData = {
        first_name: profile.first_name || '',
        middle_name: profile.middle_name || '',
        last_name: profile.last_name || '',
        display_name: profile.display_name || '',
        bio: profile.bio || '',
        date_of_birth: profile.date_of_birth ? profile.date_of_birth.split('T')[0] : '',
        gender: profile.gender || '',
        location: profile.location || '',
        language: profile.language || 'ru',
        timezone: profile.timezone || 'UTC',
      };
      setFormData(data);
      setOriginalData(data);
    }
  }, [profile]);

  // Check for changes
  useEffect(() => {
    const hasChanges = Object.keys(formData).some(
      key => formData[key as keyof typeof formData] !== originalData[key as keyof typeof originalData]
    );
    onMarkChanged(hasChanges);
  }, [formData, originalData, onMarkChanged]);

  const handleInputChange = (field: keyof ProfileFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    if (!profile?.id) {
      toast({
        title: t('settings.profile.noProfileError'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const updateRequest: UpdateUserProfileRequest = {
        profile: {
          first_name: formData.first_name,
          middle_name: formData.middle_name,
          last_name: formData.last_name,
          display_name: formData.display_name,
          bio: formData.bio,
          date_of_birth: formData.date_of_birth || undefined,
          gender: formData.gender,
          location: formData.location,
          language: formData.language,
          timezone: formData.timezone,
        },
      };

      await api.UserprofileApi.updateUserProfile(profile.id, updateRequest);
      
      // Update original data
      setOriginalData(formData);
      onClearChanges();
      
      toast({
        title: t('settings.profile.saveSuccess'),
        description: t('settings.profile.saveSuccessMessage'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      // Reload profile data
      await reloadProfile();
    } catch (error: any) {
      console.error('Failed to save profile data:', error);
      toast({
        title: t('settings.profile.saveError'),
        description: error?.response?.data?.message || error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData(originalData);
  };

  if (!profile) {
    return (
      <Alert status="warning">
        <AlertIcon />
        {t('settings.profile.noProfile')}
      </Alert>
    );
  }

  const hasChanges = Object.keys(formData).some(
    key => formData[key as keyof typeof formData] !== originalData[key as keyof typeof originalData]
  );

  return (
    <VStack spacing={6} align="stretch">
      {/* Header */}
      <VStack spacing={2} align="flex-start">
        <Heading size="md">{t('settings.profile.title')}</Heading>
        <Text color="gray.600" fontSize="sm">
          {t('settings.profile.description')}
        </Text>
      </VStack>

      {/* Personal Information */}
      <Card bg="bg.surface" shadow="sm" border="1px solid" borderColor="border.default">
        <CardBody>
          <VStack spacing={4} align="stretch">
            <Heading size="sm">{t('settings.profile.personalInfo')}</Heading>
            
            <HStack spacing={4}>
              <FormControl flex={1}>
                <FormLabel>{t('settings.profile.firstName')}</FormLabel>
                <Input
                  value={formData.first_name}
                  onChange={(e) => handleInputChange('first_name', e.target.value)}
                  placeholder={t('settings.profile.firstNamePlaceholder')}
                />
              </FormControl>

              <FormControl flex={1}>
                <FormLabel>{t('settings.profile.middleName')}</FormLabel>
                <Input
                  value={formData.middle_name}
                  onChange={(e) => handleInputChange('middle_name', e.target.value)}
                  placeholder={t('settings.profile.middleNamePlaceholder')}
                />
              </FormControl>
            </HStack>

            <HStack spacing={4}>
              <FormControl flex={1}>
                <FormLabel>{t('settings.profile.lastName')}</FormLabel>
                <Input
                  value={formData.last_name}
                  onChange={(e) => handleInputChange('last_name', e.target.value)}
                  placeholder={t('settings.profile.lastNamePlaceholder')}
                />
              </FormControl>

              <FormControl flex={1}>
                <FormLabel>{t('settings.profile.displayName')}</FormLabel>
                <Input
                  value={formData.display_name}
                  onChange={(e) => handleInputChange('display_name', e.target.value)}
                  placeholder={t('settings.profile.displayNamePlaceholder')}
                />
              </FormControl>
            </HStack>

            <FormControl>
              <FormLabel>{t('settings.profile.bio')}</FormLabel>
              <Textarea
                value={formData.bio}
                onChange={(e) => handleInputChange('bio', e.target.value)}
                placeholder={t('settings.profile.bioPlaceholder')}
                rows={4}
              />
            </FormControl>

            <HStack spacing={4}>
              <FormControl flex={1}>
                <FormLabel>{t('settings.profile.dateOfBirth')}</FormLabel>
                <Input
                  value={formData.date_of_birth}
                  onChange={(e) => handleInputChange('date_of_birth', e.target.value)}
                  type="date"
                />
              </FormControl>

              <FormControl flex={1}>
                <FormLabel>{t('settings.profile.gender')}</FormLabel>
                <Select
                  value={formData.gender}
                  onChange={(e) => handleInputChange('gender', e.target.value)}
                  placeholder={t('settings.profile.selectGender')}
                >
                  <option value="male">{t('settings.profile.male')}</option>
                  <option value="female">{t('settings.profile.female')}</option>
                  <option value="other">{t('settings.profile.other')}</option>
                  <option value="prefer_not_to_say">{t('settings.profile.preferNotToSay')}</option>
                </Select>
              </FormControl>
            </HStack>

            <FormControl>
              <FormLabel>{t('settings.profile.location')}</FormLabel>
              <Input
                value={formData.location}
                onChange={(e) => handleInputChange('location', e.target.value)}
                placeholder={t('settings.profile.locationPlaceholder')}
              />
            </FormControl>
          </VStack>
        </CardBody>
      </Card>

      {/* Localization Settings */}
      <Card bg="bg.surface" shadow="sm" border="1px solid" borderColor="border.default">
        <CardBody>
          <VStack spacing={4} align="stretch">
            <Heading size="sm">{t('settings.profile.localization')}</Heading>
            
            <HStack spacing={4}>
              <FormControl flex={1}>
                <FormLabel>{t('settings.profile.language')}</FormLabel>
                <Select
                  value={formData.language}
                  onChange={(e) => handleInputChange('language', e.target.value)}
                >
                  <option value="en">{t('settings.profile.english')}</option>
                  <option value="ru">{t('settings.profile.russian')}</option>
                </Select>
              </FormControl>

              <FormControl flex={1}>
                <FormLabel>{t('settings.profile.timezone')}</FormLabel>
                <Select
                  value={formData.timezone}
                  onChange={(e) => handleInputChange('timezone', e.target.value)}
                >
                  <option value="UTC">UTC</option>
                  <option value="Europe/Moscow">Europe/Moscow</option>
                  <option value="Europe/London">Europe/London</option>
                  <option value="America/New_York">America/New_York</option>
                  <option value="America/Los_Angeles">America/Los_Angeles</option>
                  <option value="Asia/Tokyo">Asia/Tokyo</option>
                  <option value="Australia/Sydney">Australia/Sydney</option>
                </Select>
              </FormControl>
            </HStack>
          </VStack>
        </CardBody>
      </Card>

      {/* Save Actions */}
      {hasChanges && (
        <Card bg="bg.surface" shadow="sm" border="1px solid" borderColor="border.default">
          <CardBody>
            <HStack spacing={4} justify="flex-end">
              <Button
                variant="outline"
                onClick={handleReset}
                isDisabled={isSubmitting}
              >
                {t('common.reset')}
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleSave}
                isLoading={isSubmitting}
                loadingText={t('common.saving')}
              >
                {t('common.save')}
              </Button>
            </HStack>
          </CardBody>
        </Card>
      )}
    </VStack>
  );
}; 